import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal, Signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { TwoFactorEnrollResponse, TwoFactorStatusResponse } from '../models/two-factor.models';

@Injectable({
    providedIn: 'root'
})
export class TwoFactorService {
    private readonly _apiUrl: string = environment.apiUrl;
    private readonly _httpClient: HttpClient = inject(HttpClient);

    // Cached enrollment flag — drives whether the interceptor prompts for a code
    // up-front on critical actions. Populated at app init and kept in sync on enroll/disable.
    private readonly _enabled = signal<boolean>(false);
    readonly enabled: Signal<boolean> = this._enabled.asReadonly();

    // True once the status has been fetched at least once — gates the "enable 2FA" banner.
    private readonly _loaded = signal<boolean>(false);
    readonly loaded: Signal<boolean> = this._loaded.asReadonly();

    setEnabled(value: boolean): void {
        this._enabled.set(value);
        this._loaded.set(true);
    }

    /** Fetch status and cache the enrollment flag (used by the app initializer). */
    loadStatus(): Observable<TwoFactorStatusResponse> {
        return this.getStatus().pipe(tap(res => this.setEnabled(!!res?.enabled)));
    }

    getStatus(): Observable<TwoFactorStatusResponse> {
        return this._httpClient.get<TwoFactorStatusResponse>(`${ this._apiUrl }api/admin/2fa/status`);
    }

    enroll(): Observable<TwoFactorEnrollResponse> {
        return this._httpClient.post<TwoFactorEnrollResponse>(`${ this._apiUrl }api/admin/2fa/enroll`, {});
    }

    confirmEnroll(code: string): Observable<void> {
        return this._httpClient.post<void>(`${ this._apiUrl }api/admin/2fa/enroll/confirm`, { code });
    }

    disable(code: string): Observable<void> {
        return this._httpClient.delete<void>(`${ this._apiUrl }api/admin/2fa`, { body: { code } });
    }
}
